import React, { useMemo, useCallback, useEffect, useContext } from 'react'
import CartReviewTile, { LoyaltyPointsEarningConfiguration } from 'components/Pages/CartReviewPage/CartReviewTile'
import CartReviewPriceStack from 'components/Pages/CartReviewPage/CartReviewPriceStack'
import CartReviewBanner from 'components/Pages/CartReviewPage/CartReviewBanner'
import CartReviewTileImageContent from 'components/Pages/CartReviewPage/CartReviewTileImageContent'
import RoomsRemainingLabel from 'components/Common/Labels/RoomsRemainingLabel'
import CondensedCartReviewTile from 'components/Pages/CartReviewPage/CondensedCartReviewTile'
import CondensedCartReviewTileContent from 'components/Pages/CartReviewPage/CondensedCartReviewTileContent'
import CartReviewTileContent from 'components/Pages/CartReviewPage/CartReviewTileContent'
import CartReviewTileActions from 'components/Pages/CartReviewPage/CartReviewTileActions'
import LoadingCartReviewTileSkeleton from 'components/Pages/CartReviewPage/LoadingCartReviewTileSkeleton'
import { formatOccupantsShort } from 'lib/offer/occupancyUtils'
import { formatDynamicDateRange } from 'lib/datetime/dateUtils'
import { getUserCartItemDisplayTotals } from 'lib/userCart/userCartTotals'
import { getBannerTypeVariant } from 'components/Pages/CartReviewPage/CartReviewUtils'
import { isFlightItem, isInstantBookingLEHotelItem } from 'lib/checkout/checkoutUtils'
import { getFlightsTotal } from 'lib/flights/flightUtils'
import useUserCartHotelItemData from 'hooks/UserCart/useUserCartHotelItemData'
import { useAppDispatch } from 'hooks/reduxHooks'
import { userCartItemsDelete, userCartItemsSaveForLater } from 'actions/UserCartActions'
import { useUserCartItemState } from 'hooks/UserCart/useUserCartItemState'
import GeoContext from 'contexts/geoContext'
import { generateLoyaltyPointsCalculatorHotelOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'

interface Props {
  items: Array<App.Checkout.InstantBookingLEHotelItem | App.Checkout.FlightItem>
  tileVariant?: 'default' | 'condensed'
  className?: string
  hideTitleAndDates?: boolean
  hideButtons?: boolean
}

function LeHotelTile({
  items,
  tileVariant = 'default',
  className,
  hideTitleAndDates = false,
  hideButtons = false,
}: Props) {
  const dispatch = useAppDispatch()

  // Separate flight and hotel items
  const hotelItem = useMemo(() => {
    return items.find(item => isInstantBookingLEHotelItem(item))
  }, [items])

  const flightItems = useMemo(() => {
    return items.filter(item => isFlightItem(item))
  }, [items])

  const data = useUserCartHotelItemData(hotelItem!, true)
  const { deleted: flightDeleted, saveForLater: flightSaveForLater } = useUserCartItemState(flightItems[0]?.itemId)

  const formattedDateRange = useMemo(() => {
    if (!hotelItem) return false
    const dateRange = formatDynamicDateRange(hotelItem.checkIn, hotelItem.checkOut)
    if (!dateRange) return false
    return `${dateRange} · ${hotelItem.duration} Nights`
  }, [hotelItem])

  const formattedOccupancy = useMemo(() => {
    if (!hotelItem?.occupancy) return false
    return formatOccupantsShort(hotelItem.occupancy)
  }, [hotelItem?.occupancy])

  const { publicTotal: hotelPublicTotal, memberTotal: hotelMemberTotal } = useMemo(
    () => getUserCartItemDisplayTotals(data.itemTotals),
    [data?.itemTotals]
  )

  // Calculate flight prices
  const flightTotal = useMemo(() => {
    return flightItems.reduce((total, flightItem) => {
      return total + getFlightsTotal(flightItem)
    }, 0)
  }, [flightItems])

  const numberOfRoomsAssociatedWithFlight = flightItems[0]?.bundledItemIds?.length ?? 1
  const flightTotalPerRoom = flightTotal / numberOfRoomsAssociatedWithFlight

  // Calculate bundled prices (hotel + flight)
  const bundledPublicTotal = hotelPublicTotal + flightTotalPerRoom
  const bundledMemberTotal = hotelMemberTotal === 0 ? 0 : hotelMemberTotal + flightTotalPerRoom

  const bannerTypeVariant = getBannerTypeVariant(data.isPast, data.isSoldOut, data.isUnavailable, !!data.error)
  const roomsLeft = data.isSoldOut ? 0 : (data.package?.availableRooms ?? 0)
  const image = data.package?.roomType.images[0] || data.offer?.image

  const isValueBeyondNonMemberPrice = (data.itemTotals?.value ?? 0) > hotelPublicTotal
  const shouldDisplayValueUpTo = isValueBeyondNonMemberPrice && (data.package?.shouldDisplayValue === true)

  // Check if all items are saved for later
  const areAllItemsSavedForLater = data.isSavedForLater && flightSaveForLater && !flightDeleted

  const isBookable = useMemo(
    () => !flightDeleted && data.isBookable,
    [flightDeleted, data.isBookable]
  )

  // Get all item IDs from the bundle
  const allItemIds = useMemo(() => items.map(item => item.itemId), [items])

  // Auto-save for later if hotel is not bookable
  useEffect(() => {
    // Only save if explicitly enabled AND item is not bookable AND not already saved/deleted
    if (
      !data.isBookable &&
      !data.isSavedForLater &&
      !data.isDeleted &&
      !data.loading &&
      hotelItem
    ) {
      dispatch(userCartItemsSaveForLater(allItemIds))
    }
  }, [data.isBookable, data.isSavedForLater, data.isDeleted, data.loading, hotelItem, dispatch, allItemIds])

  // if flight items are saved for later then save the hotel item as well
  useEffect(() => {
    if (flightSaveForLater && !flightDeleted && !data.isSavedForLater && !data.isDeleted && hotelItem) {
      dispatch(userCartItemsSaveForLater([hotelItem.itemId]))
    }
  }, [flightSaveForLater, flightDeleted, data.isSavedForLater, data.isDeleted, hotelItem, dispatch])

  const handleSaveForLater = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // if there are multi rooms of same hotel, then save only the hotel room item, and not the flight item, but
    dispatch(userCartItemsSaveForLater(allItemIds))
  }, [dispatch, allItemIds])

  const handleDelete = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    // on removal of leHotel item, check if it has any flight items associated with it, if yes, then reduce the
    dispatch(userCartItemsDelete(allItemIds))
  }, [dispatch, allItemIds])

  const { currentRegionCode } = useContext(GeoContext)

  const loyaltyPointsEarningConfiguration = useMemo<LoyaltyPointsEarningConfiguration>(() => {
    if (!data.offer || !data.itemTotals) {
      return {
        calculationRequests: [],
        calculationType: 'exact',
      }
    }
    const calculationRequest = generateLoyaltyPointsCalculatorHotelOptions(data.offer, {
      pkg: data.package,
      price: bundledPublicTotal,
      memberPrice: bundledMemberTotal,
      currentRegionCode,
    })
    return {
      calculationRequests: [calculationRequest],
      calculationType: 'exact',
    }
  }, [data.offer, data.itemTotals, bundledPublicTotal, bundledMemberTotal, currentRegionCode, data.package])

  if (data.loading || !hotelItem) {
    return <LoadingCartReviewTileSkeleton />
  }

  if (tileVariant === 'condensed') {
    return (
      <CondensedCartReviewTile
        label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
        content={<CondensedCartReviewTileContent
          title={data.offer?.property.name || data.offer?.name || ''}
          dates={formattedDateRange}
          packageTitle={data.package?.name}
          occupancy={formattedOccupancy}
          roomName={data.package?.roomType.name}
        />}
        price={<CartReviewPriceStack
          price={bundledPublicTotal}
          memberPrice={bundledMemberTotal}
          value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
          luxPlusOfferConfig={data.offer?.luxPlus}
        />}
        banner={<CartReviewBanner variant={bannerTypeVariant} />}
        imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
        onDelete={hideButtons ? undefined : handleDelete}
      />
    )
  }
  const actions = hideButtons ? undefined : (
    <CartReviewTileActions
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      onSaveForLater={handleSaveForLater}
      onDelete={handleDelete}
    />
  )

  return (
    <CartReviewTile
      label={roomsLeft ? <RoomsRemainingLabel roomCount={roomsLeft} threshold="rate" /> : undefined}
      imageElement={image ? <CartReviewTileImageContent image={image} /> : undefined}
      content={<CartReviewTileContent
        title={data.offer?.property.name || data.offer?.name || ''}
        dates={formattedDateRange}
        hideTitleAndDates={hideTitleAndDates}
        packageTitle={data.package?.name}
        occupancy={formattedOccupancy}
        loyaltyPointsEarningConfiguration={loyaltyPointsEarningConfiguration}
        roomName={data.package?.roomType.name}
      />}
      price={<CartReviewPriceStack
        price={bundledPublicTotal}
        memberPrice={bundledMemberTotal}
        value={shouldDisplayValueUpTo ? (data.itemTotals?.value ?? 0) : 0}
        luxPlusOfferConfig={data.offer?.luxPlus}
      />}
      banner={<CartReviewBanner variant={bannerTypeVariant} />}
      isBookable={isBookable}
      saveForLater={areAllItemsSavedForLater}
      actions={actions}
      className={className}
      hideAvailabilityDetails={false}
      disableOverlay={false}
    />
  )
}

export default LeHotelTile
